import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export const STALE_SHORT_MS = 60 * 1000;
export const STALE_LONG_MS = 5 * 60 * 1000;

export const MISSING_STATUSES = [403, 404];

export const isMissingError = (error) => MISSING_STATUSES.includes(error?.status);

export const retryUnlessMissingUpTo = (maxRetries) => (failureCount, error) => !isMissingError(error) && failureCount < maxRetries;

export const retryUnlessMissing = retryUnlessMissingUpTo(1);

export const wfmKeys = {
  access: (email) => ['wfm', 'access', email],
  landing: (month) => ['wfm', 'landing', month ?? 'current'],
  node: (nodeId, month) => ['wfm', 'node', nodeId, month ?? 'current'],
  team: (teamId, month) => ['wfm', 'team', teamId, month ?? 'current'],
  teamMembers: (teamId, month) => ['wfm', 'team-members', teamId, month ?? 'current'],
  trend: (nodeId) => ['wfm', 'trend', nodeId ?? 'org'],
  population: (level, month) => ['wfm', 'population', level, month ?? 'current'],
};

export const invalidateWfmData = (client, exceptKey) => {
  const except = exceptKey ? JSON.stringify(exceptKey) : null;
  return client.invalidateQueries({
    predicate: ({ queryKey }) => queryKey[0] === 'wfm' && queryKey[1] !== 'access'
      && JSON.stringify(queryKey) !== except,
  });
};

export const keepPreviousForSameEntity = (entityId) => (previous, previousQuery) => (
  previousQuery?.queryKey[2] === entityId ? previous : undefined
);

let wfmDenied = false;

export const isWfmDenied = () => wfmDenied;
export const clearWfmDenied = () => { wfmDenied = false; };

export const denyOnForbidden = (fetcher) => async (...args) => {
  try {
    return await fetcher(...args);
  } catch (error) {
    if (error?.status === 403) wfmDenied = true;
    throw error;
  }
};

export const wfmEnabled = (enabled = true) => Boolean(enabled) && !wfmDenied;

export const monthParam = (month, isUserSelected) => (isUserSelected ? month.format('YYYY-MM') : undefined);

export const REFRESH_POLL_MS = 2000;
export const REFRESH_POLL_LIMIT_MS = 60 * 1000;

export function useWfmSnapshotQuery({ enabled = true, ...options }) {
  const [pollExpired, setPollExpired] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const query = useQuery({
    ...options,
    enabled: wfmEnabled(enabled),
    refetchInterval: (q) => (
      !pollExpired && !wfmDenied && q.state.data?.snapshot?.refreshing ? REFRESH_POLL_MS : false
    ),
  });
  const snapshot = query.data?.snapshot ?? null;
  const refreshing = snapshot?.refreshing === true;
  const client = useQueryClient();
  const seen = useRef(null);
  const builtAt = snapshot?.built_at ?? null;
  const { queryKey } = options;
  const { isPlaceholderData } = query;
  useEffect(() => {
    if (isPlaceholderData) return;
    const keyHash = JSON.stringify(queryKey);
    const previous = seen.current?.keyHash === keyHash ? seen.current : null;
    seen.current = { keyHash, builtAt, refreshing };
    const rebuilt = previous?.refreshing || (previous?.builtAt != null && previous.builtAt !== builtAt);
    if (rebuilt && !refreshing) invalidateWfmData(client, queryKey);
  }, [builtAt, refreshing, client, queryKey, isPlaceholderData]);
  useEffect(() => {
    if (!refreshing) {
      setPollExpired(false);
      return undefined;
    }
    const timer = setTimeout(() => setPollExpired(true), REFRESH_POLL_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [refreshing, attempt]);
  const { refetch } = query;
  const retry = useCallback(() => {
    setPollExpired(false);
    setAttempt((n) => n + 1);
    refetch();
  }, [refetch]);
  return {
    ...query,
    snapshotState: {
      refreshing,
      stale: snapshot?.stale === true,
      builtAt,
      pollExpired: refreshing && pollExpired,
      retry,
    },
  };
}

export function useWfmContentMark(ready) {
  const [marked, setMarked] = useState(false);
  useEffect(() => {
    if (!ready || marked) return;
    if (typeof performance !== 'undefined' && typeof performance.mark === 'function') performance.mark('wfm-content');
    setMarked(true);
  }, [ready, marked]);
}
